import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { ExportFormatButtons } from './buttons/ExportFormatButton';
import { ArtifactPageTabs } from './ArtifactPageTabs';
import { ArtifactContent } from './ArtifactContent';
import { AgentCanvasOverlay, type AgentCanvasOverlayHandle } from './AgentCanvasOverlay';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { applyTheme } from '@/artifact/theme';
import { downloadExportPayload, exportSurfaceSnapshot, type ArtifactExportFormat } from '@/artifact/export';
import type { ArtifactEngine } from '@/hooks/useArtifactEngine';
import { trackArtifactExported } from '@/analytics';

import { ConnectionStatus } from './labels/ConnectionStatus';
import { ArtifactEmptyState } from './labels/ArtifactEmptyState';

import '@/artifact/artifact.css';

const MIN_WIDTH = 280;
/** Left gutter: visible chat + AgentIsland (`left-6` ~24px + ~56px pill) + click target */
const PEEK_PX = 112;


interface ArtifactPanelProps {
  engine: ArtifactEngine;
  collapsed: boolean;
  onClose?: () => void;
  onClearAll: () => void;
  maximized: boolean;
  onMaximizedChange: (value: boolean) => void;
}

export function ArtifactPanel(props: ArtifactPanelProps) {
  const {
    engine,
    collapsed,
    onClose,
    onClearAll,
    maximized,
    onMaximizedChange,
  } = props;

  const {
    store,
    connected,
    pages,
    viewingPageIndex,
    liveComponentsSize,
    rootCuids,
    focusTarget,
    hasComponents,
    hasPages,
    callbacks,
    theme,
    setSurfaceGetter,
    setAgentCanvasController,
    deletePage,
    clearCurrentPage,
    sendAgentCanvasSubmission,
  } = engine;

  const panelRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const agentCanvasRef = useRef<AgentCanvasOverlayHandle>(null);
  const [panelWidth, setPanelWidth] = useState<number | null>(null);
  const draggingPointerId = useRef<number | null>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const [exportingFormat, setExportingFormat] = useState<ArtifactExportFormat | null>(null);

  const getSurfaceElement = useCallback((): HTMLElement | null => {
    if (!containerRef.current) return null;
    const el = containerRef.current.querySelector('.a-surface');
    return el instanceof HTMLElement ? el : null;
  }, []);

  useEffect(() => {
    if (!collapsed) {
      setSurfaceGetter(getSurfaceElement);
      return () => setSurfaceGetter(null);
    }
  }, [collapsed, getSurfaceElement, setSurfaceGetter]);

  useEffect(() => {
    if (collapsed) {
      setAgentCanvasController(null);
      return;
    }
    setAgentCanvasController({
      collectContext: async () => agentCanvasRef.current?.collectContext() ?? null,
      clear: () => agentCanvasRef.current?.clear(),
    });
    return () => setAgentCanvasController(null);
  }, [collapsed, setAgentCanvasController]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      applyTheme(theme, containerRef.current, true);
    }
  }, [theme, collapsed, maximized]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    draggingPointerId.current = e.pointerId;
    startX.current = e.clientX;
    startWidth.current = panelRef.current?.offsetWidth ?? 400;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (draggingPointerId.current === null) return;
    const delta = startX.current - e.clientX;
    setPanelWidth(Math.max(MIN_WIDTH, startWidth.current + delta));
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (draggingPointerId.current === null) return;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* already released */ }
    draggingPointerId.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const makeFilename = useCallback((format: ArtifactExportFormat): string => {
    const stamp = new Date().toISOString().replaceAll(':', '-');
    return `panel-${stamp}.${format}`;
  }, []);

  const handleDownload = useCallback(async (format: ArtifactExportFormat) => {
    const surface = getSurfaceElement();
    if (!surface || exportingFormat !== null) return;
    trackArtifactExported(format);
    setExportingFormat(format);
    try {
      const payload = await exportSurfaceSnapshot(surface, format);
      downloadExportPayload(payload, makeFilename(format));
    } finally {
      setExportingFormat(null);
    }
  }, [exportingFormat, getSurfaceElement, makeFilename]);

  if (collapsed) return null;

  const widthStyle = maximized
    ? undefined
    : panelWidth !== null
      ? { width: panelWidth, minWidth: MIN_WIDTH }
      : { width: '50%', minWidth: MIN_WIDTH };

  /** Fixed children under a `w-0` flex slot use that box as their containing block — portaled UI uses the viewport instead. */
  const panelInterior = (
    <>
      {!maximized && (
        <div
          className="absolute left-0 top-0 bottom-0 z-10 w-1.5 cursor-col-resize touch-none transition-colors hover:bg-white/[0.08] active:bg-white/[0.12]"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
      )}

      <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              {!maximized ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onMaximizedChange(true)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-white/[0.05] hover:text-zinc-300"
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={6}>
                    Expand over chat
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onMaximizedChange(false)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-white/[0.05] hover:text-zinc-300"
                    >
                      <Minimize2 className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={6}>
                    Split view
                  </TooltipContent>
                </Tooltip>
              )}
              <ConnectionStatus connected={connected} connectedLabel="Connected" disconnectedLabel="Disconnected" />
            </div>
            <div className="flex min-w-0 flex-1 justify-center" />
            <div className="flex shrink-0 items-center gap-1">
              {hasComponents && (
                <div className="flex items-center gap-0.5">
                  <ExportFormatButtons
                    formats={['png', 'pdf', 'html']}
                    disabled={exportingFormat !== null}
                    onDownload={handleDownload}
                    buttonClassName="h-6 rounded px-1.5 text-[10px] hover:bg-white/[0.05] hover:text-zinc-300"
                  />
                </div>
              )}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-6 w-6 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-white/[0.05] hover:text-zinc-300"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {(hasPages || liveComponentsSize > 0) && (
            <ArtifactPageTabs
              pages={pages}
              viewingPageIndex={viewingPageIndex}
              liveComponentsSize={liveComponentsSize}
              onSelectPage={(index) => store.dispatch({ type: 'SET_VIEWING_PAGE', index })}
              onDeletePage={(idx) => {
                const removingLastRenderablePage = pages.length === 1 && liveComponentsSize === 0;
                void deletePage(idx);
                if (removingLastRenderablePage) {
                  onClearAll?.();
                }
              }}
              onDeleteLivePage={() => {
                const removingLastRenderablePage = pages.length === 0;
                void clearCurrentPage();
                if (removingLastRenderablePage) {
                  onClearAll?.();
                }
              }}
              classNames={{
                root: "px-2 py-1",
                tab: "px-2 py-1 pr-5 text-[11px]",
                tabInactive: "hover:bg-white/[0.04] hover:text-zinc-400",
                delete: "h-4 w-4 opacity-0 transition-opacity group-hover/tab:opacity-100 hover:text-zinc-300 hover:bg-white/[0.08]",
                liveTab: "px-2 py-1 pr-5 text-[11px]",
                liveDelete: "h-4 w-4 opacity-100 hover:text-zinc-300 hover:bg-white/[0.08]",
              }}
            />
          )}

      <ArtifactContent
        store={store}
        callbacks={callbacks}
        rootCuids={rootCuids}
        focusTarget={focusTarget}
        containerRef={containerRef}
        emptyState={
          <ArtifactEmptyState
            title="Panel"
            subtitle={connected
              ? 'Waiting for the agent to render components...'
              : 'Outputs, plots, and files will appear here'}
          />
        }
        overlay={
          <AgentCanvasOverlay
            ref={agentCanvasRef}
            active={hasComponents}
            containerRef={containerRef}
            onSubmit={sendAgentCanvasSubmission}
          />
        }
      />
    </>
  );

  return (
    <div
      className={cn(
        'relative hidden h-full shrink-0 desktop:block',
        maximized && 'w-0 overflow-visible',
      )}
      style={maximized ? undefined : widthStyle}
    >
      {!maximized && (
        <aside
          ref={panelRef}
          className="relative flex h-full min-h-0 min-w-0 w-full flex-col border-l border-white/[0.04] bg-[#121212]"
        >
          {panelInterior}
        </aside>
      )}
      {maximized &&
        createPortal(
          <>
            <button
              type="button"
              aria-label="Show chat alongside panel"
              className="fixed left-0 top-0 z-[41] cursor-pointer border-0 bg-transparent p-0"
              style={{ width: PEEK_PX, height: '100dvh' }}
              onClick={() => onMaximizedChange(false)}
            />
            <aside
              ref={panelRef}
              className="fixed top-0 z-40 flex min-h-0 min-w-0 flex-col border-l border-white/[0.04] bg-[#121212] shadow-2xl"
              style={{
                left: PEEK_PX,
                right: 0,
                height: '100dvh',
              }}
            >
              {panelInterior}
            </aside>
          </>,
          document.body,
        )}
    </div>
  );
}
